import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { LogIn, Mail, AlertCircle, Eye, EyeOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import HorizontalScroller from '../components/ui/HorizontalScroller'
import LanguageSwitcher from '../components/LanguageSwitcher'
import { kanbify } from '../lib/kanbifyClient'

const AUTH_MODE_KEY = 'kanbify:auth-mode'

export default function AuthPage() {
    const { signIn, signUp, signUpViaCompanyLink, confirmSignup } = useAuth()
    const { t } = useTranslation()
    const FEATURE_ITEMS = [
        { title: t('auth.feature1') },
        { title: t('auth.feature2') },
        { title: t('auth.feature3') },
        { title: t('auth.feature4') },
    ]
    const [email, setEmail] = useState('')
    // Only lock the email when it was actually prefilled from the invitation.
    // (Old bug: readOnly was tied to "email non-empty" → typing locked after 1 char
    // whenever the invite email couldn't be prefilled.)
    const [emailPrefilled, setEmailPrefilled] = useState(false)
    const [password, setPassword] = useState('')
    const [fullName, setFullName] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [keepLoggedIn, setKeepLoggedIn] = useState(true)
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState('')

    // Forgot password dialog state
    const [showForgotDialog, setShowForgotDialog] = useState(false)
    const [forgotEmail, setForgotEmail] = useState('')
    const [forgotLoading, setForgotLoading] = useState(false)
    const [forgotMessage, setForgotMessage] = useState({ type: '', text: '' })

    // Close forgot dialog on Escape
    useEffect(() => {
        if (!showForgotDialog) return
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && !forgotLoading) setShowForgotDialog(false)
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [showForgotDialog, forgotLoading])

    // Invitation token din URL (?invite=TOKEN)
    const urlParams = new URLSearchParams(window.location.search)
    const inviteToken = urlParams.get('invite')
    const joinToken = urlParams.get('join') // per-company self-register link
    const resetToken = urlParams.get('reset_token')
    const confirmToken = urlParams.get('confirm_signup') // email-confirm join

    // Reset-password screen state (from emailed ?reset_token= link)
    const [rpNew, setRpNew] = useState('')
    const [rpNew2, setRpNew2] = useState('')
    const [rpBusy, setRpBusy] = useState(false)
    const [rpMsg, setRpMsg] = useState({ type: '', text: '' })

    // ?confirm_signup= screen state (emailed join-confirm link) — the
    // user sets their password here (form|working|error)
    const [cfStatus, setCfStatus] = useState('form')
    const [cfMsg, setCfMsg] = useState('')
    const [cfPw, setCfPw] = useState('')
    const [cfPw2, setCfPw2] = useState('')

    // Tab-ul ales de user trebuie să supraviețuiască la refresh. Altfel,
    // un link de înscriere (?invite/?join) forța mereu "Înregistrare" după
    // reload, chiar dacă userul apăsase deja "Log In".
    const [isLogin, setIsLogin] = useState(() => {
        // Registration is invitation-only. On the public root login page, never
        // restore a stale "register" tab from localStorage and never expose a
        // standalone create-account path.
        if (!inviteToken && !joinToken) return true
        try {
            const saved = localStorage.getItem(AUTH_MODE_KEY)
            if (saved === 'login') return true
            if (saved === 'register') return false
        } catch { /* localStorage unavailable */ }
        return false
    })

    const setAuthMode = (mode) => {
        const requestedRegister = mode === 'register'
        const nextIsLogin = requestedRegister && (inviteToken || joinToken) ? false : true
        setIsLogin(nextIsLogin)
        try { localStorage.setItem(AUTH_MODE_KEY, nextIsLogin ? 'login' : 'register') } catch { /* localStorage unavailable */ }
    }

    // The confirm screen now asks the user to SET a password (the ?join=
    // request no longer collected one). Submitting creates the real
    // account + logs in (SIGNED_IN → AuthContext → app). Password is only
    // ever in memory here — never stored server-side until this submit.
    const handleConfirmSubmit = async (e) => {
        e.preventDefault()
        if (cfStatus === 'working') return
        if (cfPw.length < 8) { setCfMsg(t('auth.pwMin8')); setCfStatus('error'); return }
        if (cfPw !== cfPw2) { setCfMsg(t('auth.pwMismatch')); setCfStatus('error'); return }
        setCfStatus('working'); setCfMsg('')
        try {
            await confirmSignup(confirmToken, cfPw)
            // success → SIGNED_IN fires; AuthContext sets user; this
            // screen unmounts as the app renders.
        } catch (err) {
            setCfStatus('error')
            setCfMsg(err?.message || t('auth.err.confirm_invalid'))
        }
    }

    // Validează linkul (invitație SAU înscriere firmă) la încărcare, pe SERVER
    // (anon nu poate citi company_invitations) → arătăm "expirat/folosit/invalid"
    // ÎNAINTE ca userul să completeze parola, și pre-completăm emailul.
    const [linkCheck, setLinkCheck] = useState({ checking: false, valid: null, code: null })
    useEffect(() => {
        if (!inviteToken && !joinToken) return undefined
        let cancelled = false
        setLinkCheck({ checking: true, valid: null, code: null })
        kanbify.auth.checkInvite({ inviteToken, joinToken }).then((r) => {
            if (cancelled) return
            setLinkCheck({ checking: false, valid: !!r?.valid, code: r?.code || null })
            if (r?.valid && r?.email) { setEmail(r.email); setEmailPrefilled(true) }
        })
        return () => { cancelled = true }
    }, [inviteToken, joinToken])

    const linkInvalid = !linkCheck.checking && linkCheck.valid === false
    const linkErrorText = (() => {
        switch (linkCheck.code) {
            case 'invite_expired': return t('auth.err.invite_expired', { defaultValue: 'Invitația a expirat. Cere administratorului una nouă.' })
            case 'invite_used': return t('auth.err.invite_used', { defaultValue: 'Invitația a fost deja folosită. Dacă ai cont, autentifică-te.' })
            case 'invite_invalid': return t('auth.err.invite_invalid', { defaultValue: 'Invitația nu este validă. Cere administratorului una nouă.' })
            case 'join_link_invalid': return t('auth.err.join_link_invalid', { defaultValue: 'Linkul de înscriere e invalid, expirat sau revocat. Cere unul nou administratorului.' })
            default: return t('auth.err.invite_invalid', { defaultValue: 'Link invalid sau expirat. Cere administratorului unul nou.' })
        }
    })()

    const handleSubmit = async (event) => {
        event.preventDefault()
        setError('')
        setSuccess('')
        setLoading(true)

        try {
            if (isLogin) {
                await signIn(email, password, keepLoggedIn)
            } else if (joinToken) {
                // Per-company self-register link: EMAIL-CONFIRMED. The
                // account is NOT created/logged-in here — a confirm email
                // is sent; the real account is created on link click.
                await signUpViaCompanyLink(email, fullName, joinToken)
                setSuccess(t('auth.confirmEmailSent'))
            } else {
                // Invitation-only: signup requires the ?invite= token.
                if (!inviteToken) {
                    setError(t('auth.inviteOnly'))
                    return
                }
                // Succeeds + logs in immediately (shim returns a session).
                await signUp(email, password, fullName, 'employee', inviteToken)
                setSuccess(t('auth.accountCreated'))
            }
        } catch (err) {
            // Already-registered / consumed-invite / dead join link: don't
            // leave the user stuck on a dead "Create account" form — pivot
            // to Log in (email kept) and STRIP the ?invite=/?join= param so
            // a refresh doesn't bounce them back to the sign-up tab.
            if (err?.code === 'account_exists' || err?.code === 'invite_invalid' || err?.code === 'no_invite' || err?.code === 'join_link_invalid') {
                setAuthMode('login')
                setPassword('')
                // Translate by code (server message is RO-only); fall back
                // to the server message, then a generic one.
                setError(t(`auth.err.${err.code}`, { defaultValue: err.message || t('auth.alreadyHaveAccountShort') }))
                try {
                    const u = new URL(window.location.href)
                    if (u.searchParams.has('join') || u.searchParams.has('invite')) {
                        u.searchParams.delete('join')
                        u.searchParams.delete('invite')
                        window.history.replaceState({}, '', u.pathname + u.search + u.hash)
                    }
                } catch { /* best-effort URL cleanup */ }
            } else {
                setError(err.message || 'A apărut o eroare. Încearcă din nou.')
            }
        } finally {
            setLoading(false)
        }
    }

    if (confirmToken) {
        return (
            <div className="auth-page auth-page-refresh">
                <div className="auth-bg" />
                <div className="auth-shell" style={{ justifyContent: 'center' }}>
                    <section className="auth-form-section" style={{ maxWidth: 440, margin: 'auto' }}>
                        <h2 style={{ marginBottom: 4 }}>{t('auth.confirmTitle')}</h2>
                        <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>{t('auth.confirmSetPw')}</p>
                        {cfStatus === 'error' && cfMsg ? (
                            <div className="auth-error" style={{ marginBottom: 12 }}>{cfMsg}</div>
                        ) : null}
                        <form className="auth-form" onSubmit={handleConfirmSubmit}>
                            <div className="form-group">
                                <label className="form-label">{t('auth.password')}</label>
                                <input type="password" className="form-input" value={cfPw}
                                    onChange={(e) => setCfPw(e.target.value)} minLength={8} required autoFocus />
                            </div>
                            <div className="form-group">
                                <label className="form-label">{t('auth.confirmPw')}</label>
                                <input type="password" className="form-input" value={cfPw2}
                                    onChange={(e) => setCfPw2(e.target.value)} minLength={8} required />
                            </div>
                            <button type="submit" className="btn btn-primary" disabled={cfStatus === 'working'}>
                                {cfStatus === 'working' ? t('auth.confirmWorking') : t('auth.confirmActivate')}
                            </button>
                        </form>
                    </section>
                </div>
            </div>
        )
    }

    if (resetToken) {
        return (
            <div className="auth-page auth-page-refresh">
                <div className="auth-bg" />
                <div className="auth-shell" style={{ justifyContent: 'center' }}>
                    <section className="auth-form-section" style={{ maxWidth: 440, margin: 'auto' }}>
                        <form
                            className="auth-form"
                            onSubmit={async (e) => {
                                e.preventDefault()
                                if (rpBusy) return
                                if (rpNew.length < 8) { setRpMsg({ type: 'error', text: 'Parola trebuie să aibă minim 8 caractere' }); return }
                                if (rpNew !== rpNew2) { setRpMsg({ type: 'error', text: 'Parolele nu coincid' }); return }
                                setRpBusy(true); setRpMsg({ type: '', text: '' })
                                const { error } = await kanbify.auth.confirmRecovery(resetToken, rpNew)
                                setRpBusy(false)
                                if (error) { setRpMsg({ type: 'error', text: error.message || 'Link invalid sau expirat' }); return }
                                setRpMsg({ type: 'success', text: 'Parolă schimbată! Te poți autentifica acum.' })
                                setTimeout(() => { window.location.href = window.location.origin }, 2500)
                            }}
                        >
                            <h2 style={{ marginBottom: 4 }}>Setează parolă nouă</h2>
                            <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>
                                Alege o parolă nouă pentru contul tău Kanbify.
                            </p>
                            {rpMsg.text && <div className={`auth-dialog-msg ${rpMsg.type}`}>{rpMsg.text}</div>}
                            <div className="form-group">
                                <label className="form-label">Parolă nouă</label>
                                <input type="password" className="form-input" value={rpNew}
                                    onChange={(e) => setRpNew(e.target.value)} autoFocus minLength={8} required />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Confirmă parola</label>
                                <input type="password" className="form-input" value={rpNew2}
                                    onChange={(e) => setRpNew2(e.target.value)} minLength={8} required />
                            </div>
                            <button type="submit" className="btn btn-primary" disabled={rpBusy}
                                style={{ width: '100%', marginTop: 8 }}>
                                {rpBusy ? 'Se salvează…' : 'Schimbă parola'}
                            </button>
                            <button type="button" className="btn-link" style={{ marginTop: 12 }}
                                onClick={() => { window.location.href = window.location.origin }}>
                                Înapoi la autentificare
                            </button>
                        </form>
                    </section>
                </div>
            </div>
        )
    }

    return (
        <div className="auth-page auth-page-refresh">
            <div className="auth-bg" />

            <div className="auth-shell">
                <section className="auth-showcase">
                    <div className="auth-showcase-brand">
                        <div className="auth-logo-icon">K</div>
                        <div className="auth-logo-text">Kanbify</div>
                    </div>

                    <h1>{t('auth.brandHeadline1')} <span className="accent">{t('auth.brandHeadline2')}</span>.</h1>

                    <p className="auth-showcase-copy">
                        {t('auth.brandTagline')}
                    </p>

                    <div className="auth-feature-list">
                        {FEATURE_ITEMS.map((item) => (
                            <article key={item.title} className="auth-feature-card">
                                <h3>{item.title}</h3>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="auth-card auth-card-refresh">
                    <div className="auth-card-header">
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                            <div>
                                <p className="auth-card-kicker">{t('auth.welcomeBack').toUpperCase()}</p>
                                <h2>{isLogin ? t('auth.loginToContinue') : t('auth.createAccount')}</h2>
                            </div>
                            <LanguageSwitcher variant="auth" />
                        </div>
                        <p className="auth-subtitle">{t('auth.accessWorkspace')}</p>
                    </div>

                    {/* Tab-urile apar la token de invitație SAU link de firmă (Log In + Înregistrare) */}
                    {(inviteToken || joinToken) ? (
                        <HorizontalScroller
                            className="compact-scroller"
                            viewportClassName="auth-tabs"
                            scrollStep={180}
                            label="Auth tabs"
                        >
                            <button
                                type="button"
                                className={`auth-tab ${isLogin ? 'active' : ''}`.trim()}
                                onClick={() => { setAuthMode('login'); setError(''); setSuccess('') }}
                            >
                                <LogIn size={16} />
                                {t('auth.logIn')}
                            </button>
                            <button
                                type="button"
                                className={`auth-tab ${!isLogin ? 'active' : ''}`.trim()}
                                onClick={() => { setAuthMode('register'); setError(''); setSuccess('') }}
                            >
                                <Mail size={16} />
                                {t('auth.register')}
                            </button>
                        </HorizontalScroller>
                    ) : null}


                    {!isLogin && linkInvalid ? <div className="auth-error">{linkErrorText}</div> : null}
                    {error ? <div className="auth-error">{error}</div> : null}
                    {success ? <div className="auth-success">{success}</div> : null}

                    <form onSubmit={handleSubmit} className="auth-form">
                        {!isLogin ? (
                            <div className="form-group">
                                <label className="form-label">{t('auth.fullName')}</label>
                                <input
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    required={!isLogin}
                                />
                            </div>
                        ) : null}

                        <div className="form-group">
                            <label className="form-label">{t('auth.email')}</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="tu@companie.com"
                                required
                                readOnly={!isLogin && Boolean(inviteToken) && emailPrefilled}
                            />
                        </div>

                        {/* Join signup sets the password on the email-confirm
                            screen, not here — hide it only for that flow. */}
                        {!(joinToken && !isLogin) && (
                        <div className="form-group">
                            <label className="form-label">{t('auth.password')}</label>
                            <div className="auth-input-wrap">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                    minLength={8}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault()
                                            e.target.closest('form')?.requestSubmit()
                                        }
                                    }}
                                />
                                <button
                                    type="button"
                                    className="auth-eye-btn"
                                    onClick={() => setShowPassword((v) => !v)}
                                    aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                                    title={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>
                        )}

                        {isLogin && (
                            <label className="auth-keep-logged">
                                <input
                                    type="checkbox"
                                    checked={keepLoggedIn}
                                    onChange={(e) => setKeepLoggedIn(e.target.checked)}
                                />
                                <span>{t('auth.keepLoggedIn')}</span>
                            </label>
                        )}

                        <button type="submit" className="auth-submit" disabled={loading || (!isLogin && (linkInvalid || linkCheck.checking))}>
                            {loading ? t('common.loading') : isLogin ? t('auth.logIn') : t('auth.createAccount')}
                        </button>
                    </form>

                    {isLogin && (
                        <div className="auth-forgot-password">
                            <button
                                type="button"
                                className="auth-forgot-btn"
                                onClick={() => {
                                    setForgotEmail(email) // pre-fill din login dacă e completat
                                    setForgotMessage({ type: '', text: '' })
                                    setShowForgotDialog(true)
                                }}
                            >
                                {t('auth.forgotPassword')}
                            </button>
                        </div>
                    )}

                    {/* Forgot Password Dialog — portal to body: .auth-shell has
                        backdrop-filter+overflow:hidden which traps position:fixed */}
                    {showForgotDialog && createPortal(
                        <div className="auth-dialog-overlay" onClick={() => !forgotLoading && setShowForgotDialog(false)}>
                            <form className="auth-dialog" onClick={(e) => e.stopPropagation()} onSubmit={async (e) => {
                                e.preventDefault()
                                if (forgotLoading || !forgotEmail) return
                                setForgotLoading(true)
                                setForgotMessage({ type: '', text: '' })
                                const { error: resetErr } = await kanbify.auth.resetPasswordForEmail(forgotEmail.trim(), {
                                    redirectTo: `${window.location.origin}/reset-password`,
                                })
                                setForgotLoading(false)
                                if (resetErr) {
                                    setForgotMessage({ type: 'error', text: resetErr.message })
                                } else {
                                    setForgotMessage({ type: 'success', text: 'Link trimis! Verifică inbox-ul (și spam).' })
                                    setTimeout(() => setShowForgotDialog(false), 3000)
                                }
                            }}>
                                <h3>Resetare parolă</h3>
                                <p className="auth-dialog-desc">
                                    Introdu adresa de email asociată contului tău și îți vom trimite un link de resetare.
                                </p>

                                {forgotMessage.text && (
                                    <div className={`auth-dialog-msg ${forgotMessage.type}`}>
                                        {forgotMessage.text}
                                    </div>
                                )}

                                <div className="form-group">
                                    <label className="form-label">Email</label>
                                    <input
                                        type="email"
                                        value={forgotEmail}
                                        onChange={(e) => setForgotEmail(e.target.value)}
                                        placeholder="tu@companie.com"
                                        autoFocus
                                        disabled={forgotLoading}
                                    />
                                </div>

                                <div className="auth-dialog-actions">
                                    <button
                                        type="button"
                                        className="auth-dialog-cancel"
                                        onClick={() => setShowForgotDialog(false)}
                                        disabled={forgotLoading}
                                    >
                                        Anulează
                                    </button>
                                    <button
                                        type="submit"
                                        className="auth-dialog-submit"
                                        disabled={forgotLoading || !forgotEmail}
                                    >
                                        {forgotLoading ? 'Se trimite...' : 'Trimite link'}
                                    </button>
                                </div>
                            </form>
                        </div>,
                        document.body
                    )}

                    <div className="auth-footer">
                        {isLogin && (inviteToken || joinToken) ? (
                            <p>
                                {joinToken ? t('auth.receivedJoinLink') : t('auth.receivedInvite')}{' '}
                                <button type="button" onClick={() => setAuthMode('register')}>{t('auth.createAccount')}</button>
                            </p>
                        ) : null}
                        {!isLogin ? (
                            <p>
                                {t('auth.alreadyHaveAccount')}{' '}
                                <button type="button" onClick={() => setAuthMode('login')}>{t('auth.logIn')}</button>
                            </p>
                        ) : null}
                    </div>
                </section>
            </div>
        </div>
    )
}
